import React, { useEffect, useState } from 'react';
import Summary from './Summary';
import Submission from './Submission';
import { UserType, SummaryType } from '../../common/commonTypes';
import { getDailySummary, getWeeklyAverages } from '../../api';
import CircularProgress from '@mui/material/CircularProgress';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import CalculatorModal from '../DrawerContent/CalculatorModal';
import Snackbar from '@mui/material/Snackbar';

const emptySummary: SummaryType = {
    calories: 0,
    protein: 0,
    fats: 0,
    carbsNoSugars: 0,
    sugars: 0,
};

type HomeTabType = {
    user: UserType;
};

const HomeTab = (props: HomeTabType) => {
    const { user } = props;
    const { goals, macrosChartType } = user.settings;
    const { t, i18n } = useTranslation();

    const [loading, setLoading] = useState<boolean>(true);
    const [dailySummary, setDailySummary] = useState<SummaryType>(emptySummary);
    const [weeklyAverages, setWeeklyAverages] = useState<SummaryType>(emptySummary);

    const [showCalculatorModal, setShowCalculatorModal] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const fetchSummaries = async () => {
        try {
            const [dailyResponse, weeklyResponse] = await Promise.all([
                getDailySummary(user.id),
                getWeeklyAverages(user.id),
            ]);
            setDailySummary(dailyResponse.data.dailySummary);
            setWeeklyAverages(weeklyResponse.data.weeklyAverages);
        } catch (error) {
            alert('Failed to load summaries.'); // todo ERROR BANNER
            console.error('Error fetching summaries:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSummaries();

        if (!user.settings.goals.calories || user.settings.goals.protein === 0) {
            setShowCalculatorModal(true);
        }
    }, []);

    return (
        <>
            {loading && (
                <div className="mt-40 text-center">
                    <CircularProgress color="primary" />
                </div>
            )}

            {!loading && (
                <>
                    {!dailySummary.calories && !weeklyAverages.calories ? (
                        <div className="h-full flex flex-col items-center justify-center text-center text-gray-700 pb-[145px]">
                            <img
                                src="/avatar_200x200_home.png"
                                alt=""
                                className="block w-50 h-50 mb-4"
                            />
                            <div className="text-sm">
                                {t('hello')}, {user.firstName}!
                            </div>
                            <div className="text-xs mt-2">{t('lets_track')}</div>
                            <div className="text-xs mt-2 px-2">
                                <Trans i18nKey={'use_input'} />
                            </div>
                            <div className="p-4 flex flex-col items-center">
                                <Button
                                    onClick={() => setShowCalculatorModal(true)}
                                    variant="contained"
                                    size="small"
                                    sx={{ textTransform: 'none' }}
                                >
                                    {t('HOME_BTN_set_my_goals')}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <Summary
                            dailySummary={dailySummary}
                            weeklyAverages={weeklyAverages}
                            goals={goals}
                            macrosChartType={macrosChartType}
                            user={user}
                        />
                    )}
                    <Submission user={user} refresh={fetchSummaries} />
                </>
            )}

            {showCalculatorModal && (
                <CalculatorModal
                    user={user}
                    onCancel={() => setShowCalculatorModal(false)}
                    onSuccess={() => {
                        setShowCalculatorModal(false);
                        setShowCalculatorModal(false);
                        setSuccessMessage(t('SUCCESS_MSG_settings_saved'));
                    }}
                />
            )}

            {successMessage && (
                <Snackbar
                    open
                    autoHideDuration={3000}
                    onClose={() => {
                        setSuccessMessage(null);
                    }}
                    message={successMessage}
                />
            )}
        </>
    );
};

export default HomeTab;
